/* PACKAGES */
import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';

/* COMPONENTS */
import FilterTools from './FilterTools';
import Pagination from '../Pagination/Pagination';

/* DATA */
import filterProducts from './filterProducts';
import { retrieveProductCache } from '../../actions/checkout';
import { updateFilter, rebaseFilter, updatePagination } from '../../actions/environment';

/* UTILS */
import { intersect, unique } from '../../utils/array';
import { translate } from '../../utils/locale';
import { rootUrl } from '../../utils/string';

/* STYLES */
import s from './Filter.scss';

class Filter extends Component {
	constructor(props) {
		super(props);

		this.state = {
			sortOpen: false,
			categories: false,
			sizes: false,
			colors: false,
			hash: typeof window !== 'undefined' ? window.location.hash : '',
		};

		this.filterContainer = React.createRef();
		this.sortContainer = React.createRef();
	}

	componentDidMount() {
		if (this.state.hash) {
			const hashFilter = [];
			this.state.hash.slice(1).split('&').forEach((filterPart) => {
				const filterParts = filterPart.split('=');
				if (filterParts.length === 1) {
					hashFilter.push({ [filterParts[0]]: true });
				} else {
					hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
				}
			});

			this.props.updateFilter(hashFilter, true, true, this.props.setup, this.props.currentPath);
			this.setState({ hash: null });
		}
	}

	handleClearFilter = (e) => {
		e.preventDefault();
		this.props.rebaseFilter(this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	handleToggleGroup = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const whichGroup = $link.dataset.group;

		this.setState((prevState) => ({
			[whichGroup]: prevState[whichGroup] ? !prevState[whichGroup] : true,
		}));
	}

	handleFilterUpdate = (e) => {
		e.preventDefault();
		const action = e.currentTarget.getAttribute('href');
		const filterType = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const filterValue = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(`/${this.props.setup.language}/`, '');

		this.props.updateFilter([{ [filterType]: filterValue }], false, false, this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	render() {
		const { data, productCache, setup, filter, currentPath, filterCategories } = this.props;
		const filtering = productCache?.[setup.market]?.[setup.pricelist];
		if (!filtering) {
			this.props.retrieveProductCache();
			return null;
		}
		const { filterArrays, filterDescriptions, localized, products } = filtering;
		const isLocalized = setup.languages && Object.keys(setup.languages).length > 1 && localized[setup.language];
		const filterCategoryUris = filterCategories.reduce((acc, fC) => {
			const category = fC.category?.[0]?.value?.[setup.language] || fC.category?.[0]?.value || '';
			return { ...acc, [category]: { ...fC } };
		}, {});
		const isFiltered = filter.filteredBy.length > 1 || (filter.filteredBy?.[0]?.categories || []).length > 1 || filterCategoryUris[currentPath];

		if (!filter.currentFilterSegment) return null;

		// Category paths are localized.
		const categories = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized?.categories?.[category] ? isLocalized.categories[category].key : category;
			return ({ ...acc, [categoryKey]: [...filterArrays.categories[category]] });
		}, {}) : filterArrays.categories;

		// Get base URI for filtering.
		if (typeof data.baseUri === 'undefined') return null;
		let baseProducts = data.baseType === 'campaigns' ? filterArrays[data.campaignType] : filterArrays[data.baseType][data.baseUri];
		if (typeof baseProducts === 'undefined') baseProducts = [];

		const filteringReturn = filterProducts({ baseProducts, filterArrays, filter, products, categories });
		const relevantBaseProducts = filteringReturn.relevantBaseProducts;
		const filteredProducts = filteringReturn.filteredProducts;
		let relevantProducts = filteringReturn.relevantProducts;

		/*
			Sorting
		*/
		if (isFiltered) {
			const sortAscending = true; // filter.sort.indexOf('descending') === -1;

			// Check that only specified categories are showing products.
			const concatenatedItems = unique(Object.keys(filterCategoryUris).reduce((acc, type) => [...(categories[type] ? categories[type] : []), ...acc], []));
			relevantProducts = intersect(relevantProducts, concatenatedItems);

			relevantProducts = relevantProducts.map((productId) => {
				let sortingValue = filter.sort.indexOf('descending') !== -1 ? 0 : 9999999999;
				if (products[productId].inStock === 0) return { key: productId, sort: sortingValue };

				let categorySortValue = Object.keys(filterCategoryUris).indexOf(rootUrl(products[productId].canonicalUri)) + 1;

				// Original sort order based on canonical category.
				if (categories?.[rootUrl(products[productId].canonicalUri)]) {
					sortingValue = categories?.[rootUrl(products[productId].canonicalUri)].indexOf(productId);
				}

				if (categorySortValue === 0) {
					Object.keys(products[productId].categories).forEach((c) => {
						const uri = products[productId].categories[c].uri;
						if (filterCategoryUris[uri]) categorySortValue = Object.keys(filterCategoryUris).indexOf(uri) + 1;
					});
				}

				sortingValue = categorySortValue * 100000 + sortingValue;

				return { key: productId, sort: sortingValue };
			}).sort((a, b) => {
				if (a.sort === b.sort) return parseInt(a.key, 10) > parseInt(b.key, 10) ? 1 : -1;
				if (sortAscending) {
					return a.sort > b.sort ? 1 : -1;
				}
				return a.sort < b.sort ? 1 : -1;
			}).map((productObject) => productObject.key);
		}

		return (
			<div className={s.filter}>
				{ /* <FilterTools setup={setup} currentPath={currentPath} relevantBaseProducts={relevantBaseProducts} filteredProducts={filteredProducts} /> */ }
				{
					isFiltered && (
						<div className={s.filter_selectedItems}>
							{
								filter.filteredBy.map((fB) => {
									const [filterCategoryValue] = Object.keys(fB);

									const filterCategory = filterDescriptions[filterCategoryValue];
									const filterValues = filterCategoryValue === 'categories' ? fB[filterCategoryValue].slice(1) : fB[filterCategoryValue];

									if (filterCategory && filterValues.length > 0) {
										return filterValues.map((fCV) => (
											<a key={`selected${filterCategoryValue}${fCV}`} className={s.filter_selectedItem} href={`#${filterCategoryValue}=${fCV}`} onClick={this.handleFilterUpdate}>
												<span>{ (filterCategory[fCV].title ? translate(setup, filterCategory[fCV].title) : filterCategory[fCV]) }</span>
												<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
													<g fill="none" fillRule="evenodd" stroke="#2B3337" strokeWidth="2" transform="translate(1 1)">
														<line x2="12" y2="12" />
														<line x1="12" y2="12" />
													</g>
												</svg>
											</a>
										));
									}

									return null;
								})
							}
							<a className={s.filter_selectedItems_clearAll} href="#clear-all" onClick={this.handleClearFilter}>
								<span>{ translate(setup, 'filterClear') }</span>
							</a>
						</div>
					)
				}
				{
					relevantProducts.length > 0 && (
						<Pagination {...this.props} setup={setup} list={relevantProducts} cache={products} currentPath={currentPath} baseUri={data.baseUri} categories={filterArrays.categories} isFiltered={isFiltered} />
					)
				}
			</div>
		);
	}
}

const mapState = ({ filter, location, environment, productCache }, { productUri = false, ...props }) => ({
	filterCategories: environment.filterCategories,
	filter,
	location,
	productUri,
	productCache,
	...props,
});

export default hot(module)(connect(mapState, { retrieveProductCache, updateFilter, rebaseFilter, updatePagination })(Filter));
